import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @mui components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";

// API service
import { createEPMLicense, updateEPMLicense } from "services/epmLicenseService";

function EndpointManagementForm({ license = null, isEdit = false }) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const initialFormData = {
    installname: "",
    installhostname: "",
    edition: "",
    endpointnumber: "",
    active: "1", // Default to active
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    color: "success",
  });

  // Edition options
  const editionOptions = [
    { value: "1", label: "Standard" },
    { value: "2", label: "Professional" },
    { value: "3", label: "Enterprise" },
    { value: "4", label: "Trial" }
  ];

  // Status options
  const statusOptions = [
    { value: "1", label: "Active" },
    { value: "0", label: "Inactive" }
  ];

  // If in edit mode, load the license data
  useEffect(() => {
    if (isEdit && license) {
      setFormData({
        installname: license.installname || "",
        installhostname: license.installhostname || "",
        edition: license.edition?.toString() || "",
        endpointnumber: license.endpointnumber?.toString() || "",
        active: license.active?.toString() || "1",
      });
    }
  }, [isEdit, license]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error for this field when user makes changes
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.installname.trim()) {
      newErrors.installname = "License name is required";
    }
    
    if (!formData.installhostname.trim()) {
      newErrors.installhostname = "Hostname is required";
    }
    
    if (!formData.edition) {
      newErrors.edition = "Edition is required";
    }
    
    if (!formData.endpointnumber) {
      newErrors.endpointnumber = "Number of endpoints is required";
    } else if (isNaN(formData.endpointnumber) || parseInt(formData.endpointnumber) < 1) {
      newErrors.endpointnumber = "Must be a valid number greater than 0";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Convert string values to numbers where appropriate
      const licenseData = {
        ...formData,
        edition: parseInt(formData.edition),
        endpointnumber: parseInt(formData.endpointnumber),
        active: parseInt(formData.active),
      };
      
      if (isEdit && license) {
        await updateEPMLicense(license.id, licenseData);
        setNotification({
          open: true,
          message: "License updated successfully!",
          color: "success",
        });
      } else {
        await createEPMLicense(licenseData);
        setNotification({
          open: true,
          message: "License created successfully!",
          color: "success",
        });
        // Reset form after successful creation
        setFormData(initialFormData);
      }
      
      // Navigate back to list after a short delay
      setTimeout(() => {
        navigate("/services/epm");
      }, 1500);
    } catch (error) {
      console.error("Error saving license:", error);
      setNotification({
        open: true,
        message: "Error saving license. Please try again.",
        color: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  return (
    <Card>
      <SoftBox p={3}>
        <SoftTypography variant="h5">
          {isEdit ? "Edit EPM License" : "Add New EPM License"}
        </SoftTypography>
      </SoftBox>
      
      <SoftBox component="form" role="form" p={3} onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.installname}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  License Name<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="installname"
                value={formData.installname}
                onChange={handleChange}
                placeholder="e.g., UDS EPM Standard - IT Dept"
                error={!!errors.installname}
              />
              {errors.installname && <FormHelperText>{errors.installname}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.installhostname}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Hostname<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="installhostname"
                value={formData.installhostname}
                onChange={handleChange}
                placeholder="e.g., epm-server.yourdomain.com"
                error={!!errors.installhostname}
              />
              {errors.installhostname && <FormHelperText>{errors.installhostname}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.edition}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Edition<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                name="edition"
                value={formData.edition}
                onChange={handleChange}
                placeholder="Select Edition"
                error={!!errors.edition}
              >
                {editionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </SoftSelect>
              {errors.edition && <FormHelperText>{errors.edition}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.endpointnumber}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Number of Endpoints<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="number"
                name="endpointnumber"
                value={formData.endpointnumber}
                onChange={handleChange}
                placeholder="e.g., 25"
                error={!!errors.endpointnumber}
              />
              {errors.endpointnumber && <FormHelperText>{errors.endpointnumber}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Status
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                name="active"
                value={formData.active}
                onChange={handleChange}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </SoftSelect>
            </FormControl>
          </Grid>
        </Grid>
        
        <SoftBox mt={4} mb={1} display="flex" justifyContent="space-between">
          <SoftButton 
            variant="outlined" 
            color="secondary" 
            onClick={() => navigate("/services/epm")}
          >
            Cancel
          </SoftButton>
          <SoftButton
            type="submit"
            variant="gradient"
            color="info"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : (isEdit ? "Update License" : "Add License")}
          </SoftButton>
        </SoftBox>
      </SoftBox>
      
      {/* Notification */}
      <SoftSnackbar
        color={notification.color}
        icon="notifications"
        title="Notification"
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
      />
    </Card>
  );
}

EndpointManagementForm.propTypes = {
  license: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default EndpointManagementForm;